import { useState, useRef } from "react";
import { businessProfile, businessStats } from "../../../mocks/businessData";
import { supabase } from "../../../lib/supabase";
import { useUser } from "../../../contexts/UserContext";

export default function ProfileSection() {
  const { user } = useUser();
  const [profile, setProfile] = useState(businessProfile);
  const [saved, setSaved] = useState(false);
  const [hours, setHours] = useState(businessProfile.openingHours);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState(businessProfile.tags);

  // Upload states
  const [coverUploading, setCoverUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const coverInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };
  const removeTag = (t: string) => setTags(tags.filter((x) => x !== t));

  const toggleDayClosed = (idx: number) => {
    setHours((prev) => prev.map((h, i) => i === idx ? { ...h, isClosed: !h.isClosed } : h));
  };
  const updateHour = (idx: number, field: "open" | "close", val: string) => {
    setHours((prev) => prev.map((h, i) => i === idx ? { ...h, [field]: val } : h));
  };

  const uploadImage = async (
    file: File,
    type: "cover" | "logo",
  ): Promise<string | null> => {
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      setUploadError("Sadece JPG, PNG veya WEBP yüklenebilir.");
      return null;
    }
    const maxSize = type === "cover" ? 5 * 1024 * 1024 : 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError(`Dosya ${type === "cover" ? "5MB" : "2MB"}'dan büyük olamaz.`);
      return null;
    }
    const ext = file.type.split("/")[1].replace("jpeg", "jpg");
    const userId = user?.id ?? "business";
    const filePath = `business/${userId}/${type}.${ext}`;
    const { error } = await supabase.storage
      .from("avatars")
      .upload(filePath, file, { upsert: true, contentType: file.type });
    if (error) {
      setUploadError("Yükleme hatası: " + error.message);
      return null;
    }
    const { data } = supabase.storage.from("avatars").getPublicUrl(filePath);
    return `${data.publicUrl}?v=${Date.now()}`;
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError("");
    setCoverUploading(true);
    const url = await uploadImage(file, "cover");
    if (url) setProfile((p) => ({ ...p, coverImage: url }));
    setCoverUploading(false);
    e.target.value = "";
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError("");
    setLogoUploading(true);
    const url = await uploadImage(file, "logo");
    if (url) setProfile((p) => ({ ...p, logo: url }));
    setLogoUploading(false);
    e.target.value = "";
  };

  return (
    <div className="space-y-6">
      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {businessStats.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 border border-stone-100">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl mb-3 ${s.color}`}>
              <i className={`${s.icon} text-base`} />
            </div>
            <div className="text-xl font-bold text-stone-800 mb-0.5">{s.value}</div>
            <div className="text-xs text-stone-400">{s.label}</div>
            <div className={`text-xs font-medium mt-1 ${s.trend === "up" ? "text-emerald-600" : "text-stone-400"}`}>
              {s.change}
            </div>
          </div>
        ))}
      </div>

      {/* Upload error */}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500" />
          <p className="text-sm text-red-600">{uploadError}</p>
          <button onClick={() => setUploadError("")} className="ml-auto text-red-400 hover:text-red-600 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      {/* Cover image */}
      <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
        <div className="relative h-40 bg-stone-100">
          {profile.coverImage ? (
            <img src={profile.coverImage} alt="" className="w-full h-full object-cover object-top" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <i className="ri-image-line text-4xl text-stone-300" />
            </div>
          )}
          {/* Cover upload overlay */}
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <label className="cursor-pointer">
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleCoverUpload}
              />
              <span className={`flex items-center gap-2 bg-white/90 hover:bg-white text-stone-700 text-xs font-semibold px-4 py-2 rounded-xl transition-colors ${coverUploading ? "opacity-60 pointer-events-none" : ""}`}>
                {coverUploading ? (
                  <><div className="w-3.5 h-3.5 border-2 border-stone-400 border-t-transparent rounded-full animate-spin" /> Yükleniyor...</>
                ) : (
                  <><i className="ri-camera-line" /> Kapak Fotoğrafını Değiştir</>
                )}
              </span>
            </label>
          </div>
        </div>

        <div className="px-6 py-4 flex items-center gap-4">
          {/* Logo upload */}
          <div className="relative -mt-10 flex-shrink-0">
            <div className="w-16 h-16 rounded-2xl overflow-hidden border-4 border-white shadow-sm bg-stone-100">
              {profile.logo ? (
                <img src={profile.logo} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <i className="ri-store-2-line text-2xl text-stone-300" />
                </div>
              )}
            </div>
            <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              {logoUploading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <i className="ri-camera-line text-white text-sm" />
              )}
            </label>
          </div>

          <div>
            <h2 className="font-bold text-stone-800">{profile.name}</h2>
            <p className="text-sm text-stone-400">{profile.category} · {profile.district}</p>
            <p className="text-xs text-stone-400 mt-0.5">Logo için üzerine gelin · Kapak için yukarıdaki butonu kullanın</p>
          </div>
          <div className="ml-auto flex gap-2">
            {profile.isEditorPick && (
              <span className="px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">Editör Seçimi</span>
            )}
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${profile.isPublished ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
              {profile.isPublished ? "Yayında" : "Taslak"}
            </span>
          </div>
        </div>
      </div>

      {/* Edit Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left: Basic Info */}
        <div className="bg-white rounded-2xl border border-stone-100 p-5">
          <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
            <i className="ri-information-line text-emerald-600" /> Temel Bilgiler
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">İşletme Adı</label>
              <input type="text" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-stone-500 mb-1.5">Kategori</label>
                <select value={profile.category} onChange={(e) => setProfile({ ...profile, category: e.target.value })}
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none cursor-pointer text-stone-700">
                  <option>Lezzet Durakları</option>
                  <option>Tarihi Yerler</option>
                  <option>Eğlence</option>
                  <option>Konaklama</option>
                  <option>Doğa ve Rotalar</option>
                  <option>Gezilecek Yerler</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-stone-500 mb-1.5">İlçe</label>
                <select value={profile.district} onChange={(e) => setProfile({ ...profile, district: e.target.value })}
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none cursor-pointer text-stone-700">
                  <option>Osmangazi</option><option>Yıldırım</option><option>Nilüfer</option>
                  <option>Mudanya</option><option>Karacabey</option><option>Gemlik</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Adres</label>
              <input type="text" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-stone-500 mb-1.5">Enlem (Latitude)</label>
                <input
                  type="number"
                  step="0.000001"
                  placeholder="40.1826"
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs text-stone-500 mb-1.5">Boylam (Longitude)</label>
                <input
                  type="number"
                  step="0.000001"
                  placeholder="29.0665"
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors"
                />
              </div>
            </div>
            <div className="bg-stone-50 rounded-xl px-4 py-3 flex items-start gap-2">
              <i className="ri-information-line text-stone-400 text-sm mt-0.5 flex-shrink-0" />
              <p className="text-xs text-stone-500">Enlem ve boylam bilgileri, kullanıcılara mekanınızın uzaklığını otomatik hesaplamak için kullanılır. Google Maps üzerinden koordinatlarınızı bulabilirsiniz.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-stone-500 mb-1.5">Telefon</label>
                <input type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs text-stone-500 mb-1.5">E-posta</label>
                <input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Web Sitesi</label>
              <input type="text" value={profile.website} onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Fiyat Aralığı</label>
              <div className="flex gap-2">
                {["Ücretsiz","₺","₺₺","₺₺₺","₺₺₺₺"].map((p) => (
                  <button key={p} onClick={() => setProfile({ ...profile, priceRange: p })}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${profile.priceRange === p ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            {/* Tags */}
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Etiketler</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((tag) => (
                  <span key={tag} className="flex items-center gap-1 px-2.5 py-1 bg-stone-100 text-stone-600 rounded-full text-xs">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="text-stone-400 hover:text-red-500 cursor-pointer"><i className="ri-close-line text-xs" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addTag()}
                  placeholder="Etiket ekle..." className="flex-1 px-4 py-2 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400" />
                <button onClick={addTag} className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">Ekle</button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Description + Hours */}
        <div className="space-y-5">
          {/* Description */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5">
            <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <i className="ri-file-text-line text-emerald-600" /> Açıklama
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-stone-500 mb-1.5">Kısa Açıklama</label>
                <input type="text" value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs text-stone-500 mb-1.5">Uzun Açıklama</label>
                <textarea value={profile.longDescription} onChange={(e) => setProfile({ ...profile, longDescription: e.target.value })}
                  rows={5} maxLength={500}
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 resize-none transition-colors" />
                <p className="text-xs text-stone-400 text-right mt-1">{profile.longDescription.length}/500</p>
              </div>
            </div>
          </div>

          {/* Opening Hours */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5">
            <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <i className="ri-time-line text-emerald-600" /> Çalışma Saatleri
            </h3>
            <div className="space-y-2">
              {hours.map((h, idx) => (
                <div key={h.day} className="flex items-center gap-3">
                  <button onClick={() => toggleDayClosed(idx)}
                    className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center cursor-pointer transition-colors ${h.isClosed ? "border-stone-200 bg-stone-50" : "border-emerald-500 bg-emerald-500"}`}>
                    {!h.isClosed && <i className="ri-check-line text-white text-xs" />}
                  </button>
                  <span className="text-sm text-stone-600 w-24 flex-shrink-0">{h.day}</span>
                  {h.isClosed ? (
                    <span className="text-xs text-stone-400 italic">Kapalı</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input type="time" value={h.open} onChange={(e) => updateHour(idx, "open", e.target.value)}
                        className="px-2 py-1 border border-stone-200 rounded-lg text-xs outline-none focus:border-emerald-400 cursor-pointer" />
                      <span className="text-stone-300 text-xs">–</span>
                      <input type="time" value={h.close} onChange={(e) => updateHour(idx, "close", e.target.value)}
                        className="px-2 py-1 border border-stone-200 rounded-lg text-xs outline-none focus:border-emerald-400 cursor-pointer" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button onClick={handleSave}
          className={`flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
            saved ? "bg-emerald-600 text-white" : "bg-emerald-700 hover:bg-emerald-800 text-white"
          }`}>
          <i className={`${saved ? "ri-check-line" : "ri-save-line"} text-base`} />
          {saved ? "Kaydedildi!" : "Değişiklikleri Kaydet"}
        </button>
      </div>
    </div>
  );
}
